import React, { useEffect, useMemo, useRef, useState } from "react";
import {useNavigate, useLocation} from "react-router-dom";

import "../styles/ChatsPage.css";

import {useUser} from "../context/UserContext.js";
import {toast} from "react-hot-toast";
import formatDate from "../_utils/formatDate.js";

import * as queryService from "../_services/query.service.js";

const ChatsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const {user} = useUser();
  const [queries, setQueries] = useState([]);
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [chats, setChats] = useState([]);
  const [chat, setChat] = useState("");
  const [search, setSearch] = useState("");
  const [loadingChats, setLoadingChats] = useState(false);
  const [sending, setSending] = useState(false);

  const chatsEndRef = useRef(null);

  const params = new URLSearchParams(location.search);
  const queryId = params.get("queryId") || null;

  useEffect(() => {
    if(selectedQuery) {
      params.set("queryId", selectedQuery.queryId);
      navigate(`/chats/?queryId=${selectedQuery.queryId}`);
      loadChats(selectedQuery.queryId);
    }
  }, [selectedQuery]);

  // useEffect(() => {
  //   scrollToBottom();
  // }, [chats]);

  
  useEffect(() => {
    if(!user) return;
    
    const handleFetch = async () => {
      setLoading(false);

      try {
        const getQueries = await queryService.fetchQueries();  
        setQueries(getQueries.data);
      } catch (error) {
        toast.error(error.message || "Unable to fetch queries");
        setQueries([]);
      } finally {
        setLoading(false);
      }
    }

    handleFetch();
  }, [user]);

  useEffect(() => {
    if(!queryId || !queries) return;
    const selected = queries.find(q => q.queryId === queryId) || null;
    
    setSelectedQuery(selected)
  }, [queryId, queries]);
  

  const loadChats = async (queryId) => {
    setLoadingChats(true);
    try {
      const getChats = await queryService.fetchChats(queryId);
      setChats(getChats.data);
    } catch(error) {
      console.error("Failed to load chats:", error);
      toast.error(error.message || "Unable to fetch chats")
    } finally {
      setLoadingChats(false);
    }
  };

  const scrollToBottom = () => {
    chatsEndRef.current?.scrollIntoView({
      behavior: "smooth"
    });
  };

  const handleSubmitChat = async (event) => {
    event.preventDefault();

    if(
      !chat.trim() ||
      !selectedQuery ||
      selectedQuery.status !== "OPEN"
    ) {
      toast.success("Form not valid");
      return;
    }

    setSending(true);

    try {
      const result = await queryService.submitChat(selectedQuery.queryId, chat);
      loadChats(selectedQuery);
      setChat("");
      toast.success(result.message || "Task complete");
    } catch(error) {
      console.error("Failed to send message:", error);
      toast.error(error.message || "Unable to perform task")
    } finally {
      setSending(false);
    }
  };

  const handleDeleteMessage = async (chatId) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this message?"
    );

    if(!confirmed) {
      return;
    }

    try {
      setChats((prev) =>
        prev.filter(
          (item) => item.chatId !== chatId
        )
      );
      const result = await queryService.deleteChat(chatId);
      toast.success(result.message || "Request submitted.")
    } catch(error) {
      console.error( "Failed to delete message:", error );
      toast.error(error.message || "Unable to perform task")
    }
  };

  const filteredQueries = useMemo(() => {
    if(!search.trim()) {
      return queries;
    }

    const searchTerm = search.toLowerCase();

    return queries.filter((query) => {
      return (
        query.subject
          .toLowerCase()
          .includes(searchTerm) ||
        query.ticketNumber
          .toLowerCase()
          .includes(searchTerm)
      );
    });
  }, [
    queries,
    search
  ]);

  return (
    <div className="chats-page">
      <div className="chats-page-header">
        <div>
          <h4>Support Chats</h4>

          <p>
            Manage and follow up on your support
            queries.
          </p>
        </div>
      </div>

      <div className="chats-layout">
        <aside className="queries-sidebar">
          <div className="queries-sidebar-header">
            <h6>
              Your Queries
              <span className="m-2">
                {queries.length}
              </span></h6>

            <div>
              <button 
                className="btn btn-primary"
                onClick={() => {
                  navigate(
                    "/contact"
                  )
                }}
              >
                <i className="bi bi-plus-circle"></i>
              </button>
            </div>
            
          </div>

          <div className="query-search">
            <i className="bi bi-search" />

            <input
              type="text"
              placeholder="Search queries..."
              value={search}
              onChange={(event) =>
                setSearch(event.target.value)
              }
            />
          </div>

          <div className="queries-list">
            {loading ? (
              <div className="queries-loading">
                Loading queries...
              </div>
            ) : filteredQueries.length > 0 ? (
              filteredQueries.map((query) => (
                <button
                  key={query.queryId}
                  type="button"
                  className={`query-item ${
                    selectedQuery?.queryId ===
                    query.queryId
                      ? "query-item-active"
                      : ""
                  }`}
                  onClick={() =>
                    setSelectedQuery(query)
                  }
                >
                  <div className="query-item-icon">
                    <i className="bi bi-chat-dots" />
                  </div>

                  <div className="query-item-content">
                    <div className="query-item-top">
                      <strong>
                        {query.subject}
                      </strong>

                      {query.unreadCount > 0 && (
                        <span className="unread-count">
                          {query.unreadCount}
                        </span>
                      )}
                    </div>

                    <p>
                      {query.message}
                    </p>

                    <div className="query-item-footer">
                      <span>
                        Ticket: {query.ticketNumber}
                      </span>

                      <span
                        className={`status-badge status-${query.status.toLowerCase()}`}
                      >
                        {query.status}
                      </span>
                    </div>
                  </div>
                </button>
              ))
            ) : (
              <div className="no-queries">
                <i className="bi bi-chat-square-text" />

                <p>
                  No queries found.
                </p>
              </div>
            )}
          </div>
        </aside>

        <main className="chat-panel">
          {selectedQuery ? (
            <>
              <div className="chat-header">
                <div className="chat-header-info">
                  <div className="chat-header-icon">
                    <i className="bi bi-chat-dots" />
                  </div>

                  <div>
                    <h5>
                      {selectedQuery.subject}
                    </h5>

                    <div className="chat-header-meta">
                      <span>
                        Ticket: {selectedQuery.ticketNumber}
                      </span>

                      <span
                        className={`status-badge status-${selectedQuery.status.toLowerCase()}`}
                      >
                        {selectedQuery.status}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="chats-container" ref={chatsEndRef}>
                {loadingChats ? (
                  <div className="chats-loading">
                    Loading chats...
                  </div>
                ) : (
                  <>
                    <div
                      className="message-wrapper message-wrapper-user"
                    >
                      <div className="message-sender">
                        Query Message
                      </div>

                      <div
                        className="message-bubble message-user"
                      >
                        <p>
                          {selectedQuery.message}
                        </p>
                      </div>

                      <div className="message-footer">
                        <span>
                          {
                            formatDate(selectedQuery.createdAt, "relative")
                          }
                        </span>
                      </div>
                    </div>
                    {chats.map((chat) => {
                      const isUser =
                        chat.sender === "user";

                      return (
                        <div
                          key={chat.chatId}
                          className={`message-row ${
                            isUser
                              ? "message-row-user"
                              : "message-row-admin"
                          }`}
                        >
                          <div
                            className={`message-wrapper ${
                              isUser
                                ? "message-wrapper-user"
                                : "message-wrapper-admin"
                            }`}
                          >
                            <div className="message-sender">
                              {isUser
                                ? "You"
                                : "Magyne Support"}
                            </div>

                            <div
                              className={`message-bubble ${
                                isUser
                                  ? "message-user"
                                  : "message-admin"
                              }`}
                            >
                              <p>
                                {chat.message}
                              </p>
                            </div>

                            <div className="message-footer">
                              <span>
                                {
                                  formatDate(chat.createdAt, "relative")
                                }
                              </span>

                              {isUser && (
                                <button
                                  type="button"
                                  className="delete-message"
                                  onClick={() =>
                                    handleDeleteMessage(
                                      chat.chatId
                                    )
                                  }
                                  title="Delete message"
                                >
                                  <i className="bi bi-trash3" />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}

                  </>
                )}
              </div>

              {selectedQuery.status === "OPEN" && (
                <div className="message-composer-container">
                  <form
                    className="message-composer"
                    onSubmit={handleSubmitChat}
                  >
                    <textarea
                      value={chat}
                      placeholder="Type your message..."
                      onChange={(event) =>
                        setChat(event.target.value)
                      }
                      rows="1"
                    />

                    <button
                      type="submit"
                      disabled={
                        !chat.trim() ||
                        sending
                      }
                      aria-label="Send message"
                    >
                      {sending ? (
                        <span
                          className="spinner-border spinner-border-sm"
                        />
                      ) : (
                        <i className="bi bi-send-fill" />
                      )}
                    </button>
                  </form>
                </div>
              )}

              {selectedQuery.status !== "OPEN" && (
                <div className="chat-closed-message">
                  <i className="bi bi-lock" />

                  <span>
                    This query is closed and can no
                    longer receive new chats.
                  </span>
                </div>
              )}
            </>
          ) : (
            <div className="no-selected-query">
              <div>
                <i className="bi bi-chat-left-text" />

                <h5>
                  Select a support query
                </h5>

                <p>
                  Choose a query from the list to view
                  the conversation.
                </p>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default ChatsPage;